import React, { useEffect, useState } from "react";
import {
  Box,
  Chip,
  Grid,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
  makeStyles,
} from "@material-ui/core";
import moment from "moment";
import { useSnackbar } from "notistack";

import TableComponent from "../../components/table";
import DialogComponent from "../../components/Dialog";
import ButtonWithLoading from "../../components/ButtonWithLoading";
import { Get } from "../../actions";
import { Get_CronJobLogs_URL } from "../../constants/apiUrls";
import { getTranslation } from "../../heplers/translationHelper";
import { format } from "../../heplers/format";

const useStyles = makeStyles(() => ({
  detail: {
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    fontFamily: "monospace",
    fontSize: "13px",
    margin: 0,
  },
}));

const levelColor = (levelName) => {
  switch (levelName) {
    case "Error":
      return { backgroundColor: "#fdecea", color: "#b71c1c" };
    case "Warning":
      return { backgroundColor: "#fff4e5", color: "#8a5300" };
    default:
      return { backgroundColor: "#e8f4fd", color: "#0d3c61" };
  }
};

/**
 * The written record of one cron job, optionally narrowed to a single customer.
 */
const CronJobLogs = ({ cronJob, levels, customerId, onChangeCustomer, customers }) => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const [logs, setLogs] = useState([]);
  const [level, setLevel] = useState("");
  const [searchString, setSearchString] = useState("");
  const [fromDate, setFromDate] = useState(
    moment().subtract(30, "days").format("YYYY-MM-DD")
  );
  const [toDate, setToDate] = useState(moment().format("YYYY-MM-DD"));
  const [isLoading, setIsLoading] = useState(true);
  const [selectedLog, setSelectedLog] = useState(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    getLogs();
    // eslint-disable-next-line
  }, [cronJob?.id, customerId, page, rowsPerPage]);

  const getLogs = () => {
    setIsLoading(true);
    const params = {
      id: cronJob.id,
      searchString: searchString,
      pageNumber: page,
      pageSize: rowsPerPage,
      fromDate: fromDate,
      toDate: toDate,
    };
    if (level !== "") params.level = level;
    if (customerId) params.customerId = customerId;

    Get(
      params,
      Get_CronJobLogs_URL,
      null,
      (resp) => {
        setLogs(resp.data);
        setIsLoading(false);
      },
      (error) => {
        enqueueSnackbar(error?.data ? error.data : "internal server error", {
          variant: "error",
        });
        setIsLoading(false);
      }
    );
  };

  const applyFilter = () => {
    if (page !== 0) setPage(0);
    else getLogs();
  };

  const columns = [
    {
      id: "createdAt",
      numeric: false,
      disablePadding: true,
      label: getTranslation("Date/Time", "Date/Heure", "Datum/Zeit"),
      component: ({ row }) => (
        <Typography noWrap>{format("dateTime", row?.createdAt)}</Typography>
      ),
    },
    {
      id: "levelName",
      numeric: false,
      disablePadding: true,
      label: getTranslation("Level", "Niveau", "Level"),
      component: ({ row }) => (
        <Chip
          size="small"
          label={row?.levelName}
          style={levelColor(row?.levelName)}
        />
      ),
    },
    {
      id: "customerId",
      numeric: false,
      disablePadding: true,
      label: getTranslation("Customer", "Client", "Kunde"),
      component: ({ row }) => (
        <Typography noWrap>{row?.customerId || "-"}</Typography>
      ),
    },
    {
      id: "message",
      numeric: false,
      disablePadding: false,
      label: getTranslation("Message", "Message", "Nachricht"),
      component: ({ row }) => (
        <Tooltip title={row?.message || ""}>
          <Typography>{row?.message}</Typography>
        </Tooltip>
      ),
    },
    {
      id: "durationMs",
      numeric: true,
      disablePadding: true,
      label: getTranslation("Duration", "Durée", "Dauer"),
      component: ({ row }) => (
        <Typography noWrap>
          {row?.durationMs || row?.durationMs === 0 ? `${row.durationMs} ms` : "-"}
        </Typography>
      ),
    },
    {
      id: "actions",
      numeric: true,
      disablePadding: false,
      align: "right",
      label: getTranslation("Details", "Détails", "Details"),
    },
  ];

  return (
    <Box>
      <Box mb={2}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <TextField
              select
              fullWidth
              size="small"
              variant="outlined"
              label={getTranslation("Customer", "Client", "Kunde")}
              value={customerId || ""}
              onChange={(e) => {
                setPage(0);
                onChangeCustomer(e.target.value);
              }}
            >
              <MenuItem value="">
                {getTranslation("All customers", "Tous les clients", "Alle Kunden")}
              </MenuItem>
              {customers?.map((item) => (
                <MenuItem key={item.customerId} value={item.customerId}>
                  {item.organizationName
                    ? `${item.organizationName} (${item.customerId})`
                    : item.customerId}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              label={getTranslation("Search", "Rechercher", "Suchen")}
              value={searchString}
              onChange={(e) => setSearchString(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === "Enter") applyFilter();
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              select
              fullWidth
              size="small"
              variant="outlined"
              label={getTranslation("Level", "Niveau", "Level")}
              value={level}
              onChange={(e) => setLevel(e.target.value)}
            >
              <MenuItem value="">
                {getTranslation("All", "Tous", "Alle")}
              </MenuItem>
              {levels?.map((item) => (
                <MenuItem key={item.value} value={item.value}>
                  {item.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              type="date"
              size="small"
              variant="outlined"
              label={getTranslation("From", "Du", "Von")}
              InputLabelProps={{ shrink: true }}
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              type="date"
              size="small"
              variant="outlined"
              label={getTranslation("To", "Au", "Bis")}
              InputLabelProps={{ shrink: true }}
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={1}>
            <ButtonWithLoading
              label={getTranslation("Filter", "Filtrer", "Filtern")}
              size="medium"
              variant="contained"
              color="primary"
              onClick={() => applyFilter()}
            />
          </Grid>
        </Grid>
      </Box>

      <TableComponent
        dense
        loading={isLoading}
        colums={columns}
        count={logs?.totalCount}
        rows={logs?.data}
        page={page}
        rowsPerPage={rowsPerPage}
        handleChangePage={(event, newPage) => setPage(newPage)}
        handleChangeRowsPerPage={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        actions={[
          {
            component: (rowData) => (
              <ButtonWithLoading
                label={getTranslation("View", "Voir", "Ansehen")}
                size="small"
                variant="contained"
                color="primary"
                onClick={() => setSelectedLog(rowData)}
              />
            ),
          },
        ]}
      />

      <DialogComponent
        open={Boolean(selectedLog)}
        onClose={() => setSelectedLog(null)}
        title={getTranslation("Log Details", "Détails du journal", "Protokolldetails")}
        fullWidth
        maxWidth="md"
      >
        {selectedLog ? (
          <Box p={2}>
            <Typography variant="subtitle2">
              {format("dateTime", selectedLog.createdAt)} &nbsp;|&nbsp;{" "}
              {selectedLog.levelName}
            </Typography>
            <Box mt={1}>
              <Typography variant="body2">
                <b>{getTranslation("Source", "Source", "Quelle")}:</b>{" "}
                {selectedLog.source || "-"}
              </Typography>
              <Typography variant="body2">
                <b>{getTranslation("Customer", "Client", "Kunde")}:</b>{" "}
                {selectedLog.customerId || "-"}
              </Typography>
              <Typography variant="body2">
                <b>{getTranslation("Duration", "Durée", "Dauer")}:</b>{" "}
                {selectedLog.durationMs || selectedLog.durationMs === 0
                  ? `${selectedLog.durationMs} ms`
                  : "-"}
              </Typography>
            </Box>
            <Box mt={2}>
              <Typography variant="body2">
                <b>{getTranslation("Message", "Message", "Nachricht")}</b>
              </Typography>
              <Typography variant="body2">{selectedLog.message}</Typography>
            </Box>
            {selectedLog.detail ? (
              <Box mt={2}>
                <Typography variant="body2">
                  <b>{getTranslation("Detail", "Détail", "Detail")}</b>
                </Typography>
                <pre className={classes.detail}>{selectedLog.detail}</pre>
              </Box>
            ) : null}
          </Box>
        ) : null}
      </DialogComponent>
    </Box>
  );
};

export default CronJobLogs;
